import { useCallback, useEffect, useState } from "react";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";
import useAuth from "./useAuth";
import { EDBService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const useEDBIntegratedOrders = (edbId: string) => {
  const { token } = useAuth();
  const [isLoadingIntegratedOrders, setIsLoading] = useState(false);
  const [item, setIntegratedOrders] = useState(null);

  const fetchItem = useCallback(
    async (id: number | string, forceRefresh = false) => {
      try {
        let result;

        // If forceRefresh is true, skip cache check and always fetch fresh data
        if (!forceRefresh) {
          result = await queryClient.getQueryData([
            CacheInvalidationAction.GET_INTEGRATED_ORDERS_BY_EDB_ID,
            edbId ?? String(id),
          ]);
        }

        if (!result || forceRefresh) {
          setIsLoading(true);
          result = await queryClient.fetchQuery({
            queryKey: [
              CacheInvalidationAction.GET_INTEGRATED_ORDERS_BY_EDB_ID,
              edbId ?? String(id),
            ],

            queryFn: () =>
              EDBService.getIntegratedOrdersByEdbId(edbId ?? String(id)),
            gcTime: 60 * 1000 * 5,
          });
          await queryClient.setQueryData(
            [
              CacheInvalidationAction.GET_INTEGRATED_ORDERS_BY_EDB_ID,
              edbId ?? String(id),
            ],
            result
          );
          setIsLoading(false);
        }
        if (!result.error) {
          setIntegratedOrders(result);
        }
      } catch (error: any) {
        console.error("Error fetching integrated orders:", error);
      }
    },
    [edbId]
  );

  // Initial fetch
  useEffect(() => {
    setIntegratedOrders(null);
    if (edbId) {
      fetchItem(edbId);
    }
  }, [fetchItem, setIntegratedOrders, edbId]);

  const onDeleteIntegratedProduct = async (
    integratedOrders: EDBIntegratedOrders
  ) => {
    try {
      const { orderProducts, edbId: integratedEdbId } = integratedOrders;

      if (orderProducts.length === 0) {
        await EDBService.deleteIntegratedOrdersByEdbId(integratedEdbId);
      } else {
        await EDBService.updateIntegratedOrders(integratedOrders);
      }
      await EDBService.UpdateEDBProducts(
        token,
        { newProducts: [], integratedOrders },
        integratedEdbId
      );

      await fetchItem(integratedEdbId);
      return null;
    } catch (err: any) {
      return { error: err.message };
    }
  };

  return {
    fetchIntegratedOrders: fetchItem,
    onDeleteIntegratedProduct,
    integratedOrders: item,
    isLoadingIntegratedOrders,
  };
};

export default useEDBIntegratedOrders;
